import React, { useEffect, useState, useMemo } from 'react';
import {
  Package,
  Search,
  Trash2,
  Grid3x3,
  List,
  Table as TableIcon,
  CheckCircle2,
  XCircle,
  ImageIcon,
  Upload,
  X,
  Edit,
  Layers,
  ChevronDown,
  ChevronUp,
  Settings,
  RefreshCw
} from 'lucide-react';
import { ColumnConfigurator, ColumnConfig } from '@/components/ui/ColumnConfigurator';
import { ModuleHeader } from '@/components/layout/ModuleHeader';
import { AddButton } from '@/components/ui/AddButton';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { useAlert } from '../../components/AlertProvider';
import Carousel from '../../components/Carousel';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';

// Types
interface Consumable {
  id: number;
  title: string;
  description: string | null;
  reference: string | null;
  marque: string | null;
  gamme: string | null;
  forme_galenique: string | null;
  conditionnement: string | null;
  mini_code: number | null;
  id_compl: number | null;
  code_barre: string | null;
  qr_code: string | null;
  batch_size: number | null;
  min_order_quantity: number | null;
  volume_ml: number | null;
  base_price: number;
  prix_vente_conseille_ttc: number | null;
  currency: string;
  unit: string;
  images: string[] | null;
  is_active: boolean;
  stock_quantity: number | null;
  created_by: number;
  created_at: string;
  updated_at: string;
}

// Component
const ConsumablesTailwind: React.FC = () => {
  const { user } = useAuth();
  const { showError, showSuccess } = useAlert();
  const isSuperAdmin: boolean = user?.role === 'super_admin';
  const isBuyer: boolean = user?.role === 'client';
  const searchStorageKey = 'consumables_table_search';
  const filterStorageKey = 'consumables_table_filters';
  const storedFilters = (() => {
    try {
      return JSON.parse(localStorage.getItem(filterStorageKey) || '{}') as {
        statusFilter?: 'all' | 'active' | 'inactive';
        brandFilter?: string;
        galenicFilter?: string;
        unitFilter?: string;
      };
    } catch (e) {
      console.error('Error parsing saved filters:', e);
      return {};
    }
  })();

  const [items, setItems] = useState<Consumable[]>([]);
  const [busy, setBusy] = useState<boolean>(false);

  const [search, setSearch] = useState<string>(() => localStorage.getItem(searchStorageKey) || '');
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'table' | 'category'>(() => {
    const saved = localStorage.getItem('consumables_viewMode');
    return (saved as 'grid' | 'list' | 'table' | 'category') || 'table';
  });

  const [columns, setColumns] = useState<ColumnConfig[]>(() => {
    const saved = localStorage.getItem('consumables_table_columns');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error parsing saved columns:', e);
      }
    }
    return [
      { key: 'product', label: 'Produit', visible: true, sortable: false },
      { key: 'description', label: 'Description', visible: true, sortable: false },
      { key: 'marque', label: 'Marque', visible: true, sortable: false },
      { key: 'gamme', label: 'Gamme', visible: true, sortable: false },
      { key: 'price', label: 'Prix / Unité', visible: true, sortable: false },
      { key: 'reference', label: 'Référence', visible: true, sortable: false },
      { key: 'status', label: 'Statut', visible: !isBuyer, sortable: false },
      { key: 'actions', label: 'Actions', visible: isSuperAdmin, sortable: false },
    ];
  });
  const [showColumnConfigurator, setShowColumnConfigurator] = useState(false);

  const visibleColumnKeys = useMemo(() => {
    return columns.filter(col => col.visible).map(col => col.key);
  }, [columns]);

  // Fonction pour rendre une cellule de header selon la colonne
  const renderTableHeader = (col: ColumnConfig) => {
    const baseClass = "px-4 py-3 text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider";

    switch (col.key) {
      case 'product':
        return <th key={col.key} className={`${baseClass} text-left`}>Produit</th>;
      case 'description':
        return <th key={col.key} className={`${baseClass} text-left`}>Description</th>;
      case 'marque':
        return <th key={col.key} className={`${baseClass} text-left`}>Marque</th>;
      case 'gamme':
        return <th key={col.key} className={`${baseClass} text-left`}>Gamme</th>;
      case 'price':
        return <th key={col.key} className={`${baseClass} text-left`}>Prix / Unité</th>;
      case 'reference':
        return <th key={col.key} className={`${baseClass} text-center`}>Référence</th>;
      case 'status':
        return <th key={col.key} className={`${baseClass} text-center`}>Statut</th>;
      default:
        return null;
    }
  };

  useEffect(() => {
    localStorage.setItem(searchStorageKey, search);
  }, [search]);

  useEffect(() => {
    localStorage.setItem('consumables_viewMode', viewMode);
  }, [viewMode]);

  // Fonction pour rendre une cellule de body selon la colonne
  const renderTableCell = (col: ColumnConfig, item: Consumable) => {
    switch (col.key) {
      case 'product':
        return (
          <td key={col.key} className="px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-slate-100 dark:bg-slate-800 rounded-lg overflow-hidden flex-shrink-0">
                <Carousel
                  images={item.images || []}
                  alt={item.title}
                  className="w-full h-full"
                  showDots={false}
                  showArrows={false}
                  autoPlay={false}
                />
              </div>
              <span className="font-semibold text-slate-900 dark:text-slate-100">{item.title}</span>
            </div>
          </td>
        );
      case 'description':
        return (
          <td key={col.key} className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400 max-w-xs truncate">
            {item.description || '—'}
          </td>
        );
      case 'marque':
        return (
          <td key={col.key} className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
            {item.marque || '—'}
          </td>
        );
      case 'gamme':
        return (
          <td key={col.key} className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
            {item.gamme || '—'}
          </td>
        );
      case 'price':
        return (
          <td key={col.key} className="px-4 py-3">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1">
                <span className="font-bold text-blue-600">{item.base_price.toFixed(2)} € HT</span>
                <span className="text-sm text-slate-500">/ {item.unit}</span>
              </div>
              {item.prix_vente_conseille_ttc != null && (
                <div className="text-xs text-slate-600 dark:text-slate-400">
                  Prix de vente conseillé : <span className="font-bold text-blue-600">{item.prix_vente_conseille_ttc.toFixed(2)} € TTC</span> / {item.unit}
                </div>
              )}
              <div className="flex flex-col gap-0.5">
                <span className="text-xs text-slate-500">
                  {item.batch_size === 1 ? 'vendu à l\'unité' : `vendu en lot de ${item.batch_size} unité`}
                </span>
                {item.min_order_quantity && item.min_order_quantity > 1 && (
                  <span className="text-xs font-medium text-orange-600 dark:text-orange-400">
                    📦 Min: {item.min_order_quantity} {item.min_order_quantity === 1 ? 'Lot' : 'Lots'} de {item.batch_size} {item.unit}
                  </span>
                )}
              </div>
            </div>
          </td>
        );
      case 'reference':
        return (
          <td key={col.key} className="px-4 py-3 text-center">
            {item.stock_quantity !== null ? (
              <span className="font-bold text-slate-700 dark:text-slate-300">{item.stock_quantity}</span>
            ) : (
              <span className="text-sm text-slate-400">—</span>
            )}
          </td>
        );
      case 'status':
        return (
          <td key={col.key} className="px-4 py-3">
            <div className="flex justify-center">
              <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${item.is_active ? 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'}`}>
                {item.is_active ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                {item.is_active ? 'Actif' : 'Inactif'}
              </div>
            </div>
          </td>
        );
      default:
        return null;
    }
  };
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>(storedFilters.statusFilter ?? 'all');
  const [brandFilter, setBrandFilter] = useState<string>(storedFilters.brandFilter ?? 'all');
  const [galenicFilter, setGalenicFilter] = useState<string>(storedFilters.galenicFilter ?? 'all');
  const [unitFilter, setUnitFilter] = useState<string>(storedFilters.unitFilter ?? 'all');

  const resetAllFilters = (): void => {
    setSearch('');
    localStorage.removeItem(searchStorageKey);
    localStorage.removeItem(filterStorageKey);
    setStatusFilter('all');
    setBrandFilter('all');
    setGalenicFilter('all');
    setUnitFilter('all');
  };

  useEffect(() => {
    localStorage.setItem(filterStorageKey, JSON.stringify({
      statusFilter,
      brandFilter,
      galenicFilter,
      unitFilter
    }));
  }, [statusFilter, brandFilter, galenicFilter, unitFilter]);

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'active': return 'Actifs';
      case 'inactive': return 'Inactifs';
      default: return status;
    }
  };

  const activeFilters = useMemo(() => {
    const tags: Array<{ key: string; label: string; onClear: () => void }> = [];
    if (search.trim()) tags.push({ key: 'search', label: `Recherche: ${search}`, onClear: () => setSearch('') });
    if (statusFilter !== 'all') tags.push({ key: 'status', label: `Statut: ${getStatusLabel(statusFilter)}`, onClear: () => setStatusFilter('all') });
    if (brandFilter !== 'all') tags.push({ key: 'brand', label: `Marque: ${brandFilter}`, onClear: () => setBrandFilter('all') });
    if (galenicFilter !== 'all') tags.push({ key: 'galenic', label: `Forme: ${galenicFilter}`, onClear: () => setGalenicFilter('all') });
    if (unitFilter !== 'all') tags.push({ key: 'unit', label: `Unité: ${unitFilter}`, onClear: () => setUnitFilter('all') });
    return tags;
  }, [search, statusFilter, brandFilter, galenicFilter, unitFilter]);

  // Dynamic Config Options
  const [brands, setBrands] = useState<{ id: number; name: string }[]>([]);
  const [ranges, setRanges] = useState<{ id: number; name: string }[]>([]);
  const [formsList, setFormsList] = useState<{ id: number; name: string }[]>([]);
  const [packagings, setPackagings] = useState<{ id: number; name: string }[]>([]);
  const [units, setUnits] = useState<{ id: number; name: string }[]>([]);
  const sortedBrands: { id: number; name: string }[] = [...brands].sort(
    (first: { id: number; name: string }, second: { id: number; name: string }) => (
      first.name.localeCompare(second.name, 'fr', { sensitivity: 'base' })
    )
  );
  const sortedRanges: { id: number; name: string }[] = [...ranges].sort(
    (first: { id: number; name: string }, second: { id: number; name: string }) => (
      first.name.localeCompare(second.name, 'fr', { sensitivity: 'base' })
    )
  );
  const sortedFormsList: { id: number; name: string }[] = [...formsList].sort(
    (first: { id: number; name: string }, second: { id: number; name: string }) => (
      first.name.localeCompare(second.name, 'fr', { sensitivity: 'base' })
    )
  );
  const sortedPackagings: { id: number; name: string }[] = [...packagings].sort(
    (first: { id: number; name: string }, second: { id: number; name: string }) => (
      first.name.localeCompare(second.name, 'fr', { sensitivity: 'base' })
    )
  );
  const sortedUnits: { id: number; name: string }[] = [...units].sort(
    (first: { id: number; name: string }, second: { id: number; name: string }) => (
      first.name.localeCompare(second.name, 'fr', { sensitivity: 'base' })
    )
  );

  // Modal state
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editing, setEditing] = useState<Consumable | null>(null);
  const [imageViewer, setImageViewer] = useState<{ open: boolean; index: number }>({ open: false, index: 0 });
  const [selectedProduct, setSelectedProduct] = useState<Consumable | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; item: Consumable | null }>({ open: false, item: null });
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const [form, setForm] = useState<{
    title: string;
    description: string;
    reference: string;
    marque: string;
    gamme: string;
    forme_galenique: string;
    conditionnement: string;
    volume_ml: number | null;
    mini_code: number | null;
    id_compl: number | null;
    code_barre: string;
    qr_code: string;
    batch_size: number | null;
    min_order_quantity: number | null;
    base_price: number;
    prix_vente_conseille_ttc: number | null;
    currency: string;
    unit: string;
    images: string[];
    is_active: boolean;
    stock_quantity: number | null;
  }>({
    title: '',
    description: '',
    reference: '',
    marque: '',
    gamme: 'Huiles CBD | Full Spectrum | 7,5% 10ml',
    forme_galenique: '',
    conditionnement: 'Cartons 2 compartiments',
    volume_ml: null,
    mini_code: null,
    id_compl: null,
    code_barre: '',
    qr_code: '',
    batch_size: 1,
    min_order_quantity: 1,
    base_price: 0,
    prix_vente_conseille_ttc: null,
    currency: 'EUR',
    unit: 'fiole',
    images: [],
    is_active: true,
    stock_quantity: null
  });

  // Store selected files for upload during save
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  // Load data
  const load = async (): Promise<void> => {
    setBusy(true);
    try {
      const params: Record<string, unknown> = {};
      if (search) params.search = search;
      if (statusFilter === 'active') params.is_active = true;
      if (statusFilter === 'inactive') params.is_active = false;
      if (brandFilter !== 'all') params.marque = brandFilter;
      if (galenicFilter !== 'all') params.forme_galenique = galenicFilter;
      if (unitFilter !== 'all') params.unit = unitFilter;
      const res = await api.get('/consumables', { params });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Load consumables error:', e);
    } finally {
      setBusy(false);
    }
  };

  // Load config options
  const loadConfig = async () => {
    try {
      const [brandsRes, rangesRes, formsRes, packagingsRes, unitsRes] = await Promise.all([
        api.get('/product-config/brands?active_only=true'),
        api.get('/product-config/ranges?active_only=true'),
        api.get('/product-config/forms?active_only=true'),
        api.get('/product-config/packagings?active_only=true'),
        api.get('/product-config/units?active_only=true')
      ]);
      setBrands(brandsRes.data);
      setRanges(rangesRes.data);
      setFormsList(formsRes.data);
      setPackagings(packagingsRes.data);
      setUnits(unitsRes.data);
    } catch (error) {
      console.error('Failed to load product config options', error);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  // Recharge la liste quand la recherche ou les filtres changent (avec un léger debounce)
  useEffect(() => {
    const t = setTimeout(() => { void load(); }, 300);
    return () => clearTimeout(t);
  }, [search, statusFilter, brandFilter, galenicFilter, unitFilter]);

  // Initialize expanded categories when items change
  useEffect(() => {
    if (viewMode === 'category' && items.length > 0) {
      const keys = new Set<string>();
      items.forEach((item) => {
        const marque = item.marque || 'Sans marque';
        const gamme = item.gamme || 'Sans gamme';
        const unit = item.unit || 'Unité';
        keys.add(`${marque}|${gamme}|${unit}`);
      });
      setExpandedCategories(keys);
    }
  }, [items, viewMode]);

  // Handlers
  const openModal = (item?: Consumable): void => {
    if (item) {
      setEditing(item);
      setForm({
        title: item.title,
        description: item.description || '',
        reference: item.reference || '',
        marque: item.marque || '',
        gamme: item.gamme || '',
        forme_galenique: item.forme_galenique || '',
        conditionnement: item.conditionnement || '',
        volume_ml: item.volume_ml ?? null,
        mini_code: item.mini_code,
        id_compl: item.id_compl,
        batch_size: item.batch_size ?? 1,
        min_order_quantity: item.min_order_quantity ?? 1,
        qr_code: item.qr_code || '',
        code_barre: item.code_barre || '',
        base_price: item.base_price,
        prix_vente_conseille_ttc: item.prix_vente_conseille_ttc ?? null,
        currency: 'EUR',
        unit: item.unit,
        images: item.images || [],
        is_active: item.is_active,
        stock_quantity: item.stock_quantity,
      });
    } else {
      setEditing(null);
      setForm({
        title: '',
        description: '',
        reference: '',
        marque: '',
        gamme: 'Huiles CBD | Full Spectrum | 7,5% 10ml',
        forme_galenique: '',
        conditionnement: packagings.length > 0 ? packagings[0].name : 'Cartons 2 compartiments',
        volume_ml: null,
        mini_code: null,
        id_compl: null,
        batch_size: 1,
        min_order_quantity: 1,
        code_barre: '',
        qr_code: '',
        base_price: 0,
        prix_vente_conseille_ttc: null,
        currency: 'EUR',
        unit: 'fiole',
        images: [],
        is_active: true,
        stock_quantity: null
      });
    }
    setSelectedFiles([]);
    setShowModal(true);
  };

  const save = async (): Promise<void> => {
    // Form validation
    if (!form.title.trim()) {
      showError('Le titre est obligatoire');
      return;
    }

    if (form.base_price <= 0) {
      showError('Le prix doit être supérieur à 0');
      return;
    }

    try {
      if (editing) {
        // Check if images have been modified (added or removed)
        const originalImages = editing.images || [];
        const currentExistingUrls = form.images.filter(url => !url.startsWith('blob:'));
        const imagesModified = selectedFiles.length > 0 ||
          originalImages.length !== currentExistingUrls.length ||
          !originalImages.every(url => currentExistingUrls.includes(url));

        // Use /with-files endpoint if images have been modified (added or deleted)
        if (imagesModified) {
          const formData = new FormData();

          // Add form fields
          formData.append('title', form.title);
          formData.append('description', form.description);
          if (form.marque) formData.append('marque', form.marque);
          if (form.gamme) formData.append('gamme', form.gamme);
          if (form.forme_galenique) formData.append('forme_galenique', form.forme_galenique);
          if (form.conditionnement) formData.append('conditionnement', form.conditionnement);
          if (form.mini_code !== null) formData.append('mini_code', form.mini_code.toString());
          if (form.id_compl !== null) formData.append('id_compl', form.id_compl.toString());
          if (form.batch_size !== null) formData.append('batch_size', form.batch_size.toString());
          if (form.volume_ml !== null) formData.append('volume_ml', form.volume_ml.toString());
          if (form.code_barre) formData.append('code_barre', form.code_barre);
          formData.append('base_price', form.base_price.toString());
          if (form.prix_vente_conseille_ttc != null) formData.append('prix_vente_conseille_ttc', form.prix_vente_conseille_ttc.toString());
          formData.append('currency', 'EUR');
          formData.append('unit', form.unit);
          formData.append('is_active', form.is_active.toString());
          if (form.stock_quantity !== null) {
            formData.append('stock_quantity', form.stock_quantity.toString());
          }

          // Add existing image URLs (filter out blob URLs which are just previews)
          // Always send image_urls, even if empty, to properly handle deletions
          formData.append('image_urls', JSON.stringify(currentExistingUrls));

          // Add new files
          selectedFiles.forEach(file => {
            formData.append('image_files', file);
          });

          await api.patch(`/consumables/${editing.id}/with-files`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } else {
          // No image changes, use regular update
          // Filter out the 'images' field from the form when sending a JSON update,
          // because the backend handles images separately via the images field as a list of strings
          // but we want to avoid sending potential blob URLs if they were somehow left in 'images'
          const { images, ...formData } = form;
          await api.patch(`/consumables/${editing.id}`, formData);
        }
        showSuccess('Produit mis à jour avec succès');
      } else {
        // For creation, use /with-files endpoint if there are files
        if (selectedFiles.length > 0 || form.images.length > 0) {
          const formData = new FormData();

          // Add form fields
          formData.append('title', form.title);
          formData.append('description', form.description);
          if (form.marque) formData.append('marque', form.marque);
          if (form.gamme) formData.append('gamme', form.gamme);
          if (form.forme_galenique) formData.append('forme_galenique', form.forme_galenique);
          if (form.conditionnement) formData.append('conditionnement', form.conditionnement);
          if (form.mini_code !== null) formData.append('mini_code', form.mini_code.toString());
          if (form.id_compl !== null) formData.append('id_compl', form.id_compl.toString());
          if (form.batch_size !== null) formData.append('batch_size', form.batch_size.toString());
          if (form.volume_ml !== null) formData.append('volume_ml', form.volume_ml.toString());
          if (form.code_barre) formData.append('code_barre', form.code_barre);
          formData.append('base_price', form.base_price.toString());
          if (form.prix_vente_conseille_ttc != null) formData.append('prix_vente_conseille_ttc', form.prix_vente_conseille_ttc.toString());
          formData.append('currency', 'EUR');
          formData.append('unit', form.unit);
          formData.append('is_active', form.is_active.toString());
          if (form.stock_quantity !== null) {
            formData.append('stock_quantity', form.stock_quantity.toString());
          }

          // Add existing image URLs (filter out blob URLs which are just previews)
          const existingUrls = form.images.filter(url => !url.startsWith('blob:'));
          if (existingUrls.length > 0) {
            formData.append('image_urls', JSON.stringify(existingUrls));
          }

          // Add files
          selectedFiles.forEach(file => {
            formData.append('image_files', file);
          });

          await api.post('/consumables/with-files', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } else {
          // No files, use regular creation
          await api.post('/consumables', form);
        }
        showSuccess('Produit créé avec succès');
      }

      setShowModal(false);
      setSelectedFiles([]);
      await load();
    } catch (e: any) {
      console.error('Save error:', e);
      showError(`Erreur lors de la sauvegarde: ${e.response?.data?.detail || e.message}`);
    }
  };

  const del = (item: Consumable): void => {
    setDeleteDialog({ open: true, item });
  };

  const handleConfirmDelete = async (): Promise<void> => {
    if (!deleteDialog.item) return;

    setIsDeleting(true);
    try {
      await api.delete(`/consumables/${deleteDialog.item.id}`);
      showSuccess('Produit supprimé avec succès');
      setDeleteDialog({ open: false, item: null });
      await load();
    } catch (e: any) {
      console.error('Delete error:', e);
      showError(e.response?.data?.detail || "Une erreur est survenue lors de la suppression");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleViewDetails = (consumable: Consumable): void => {
    setSelectedProduct(consumable);
    setShowDetailsModal(true);
  };

  const closeDetailsModal = (): void => {
    setShowDetailsModal(false);
    setSelectedProduct(null);
  };

  const handleCardClick = (item: Consumable): void => {
    if (isSuperAdmin) {
      openModal(item);
    } else {
      handleViewDetails(item);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Store files for upload during save
    const newFiles: File[] = Array.from(files);
    setSelectedFiles(prev => [...prev, ...newFiles]);

    // Create preview URLs for immediate display
    const previewUrls: string[] = newFiles.map(file => URL.createObjectURL(file));
    setForm(prev => ({ ...prev, images: [...prev.images, ...previewUrls] }));
  };

  const removeImage = (idx: number): void => {
    const imageUrl = form.images[idx];

    // If it's a blob URL (preview), also remove from selectedFiles
    if (imageUrl.startsWith('blob:')) {
      const fileIndex = selectedFiles.findIndex((_, i) => {
        const previewIndex = form.images.slice(0, idx + 1).filter(url => url.startsWith('blob:')).length - 1;
        return i === previewIndex;
      });
      if (fileIndex >= 0) {
        setSelectedFiles(prev => prev.filter((_, i) => i !== fileIndex));
      }
      // Revoke the blob URL to free memory
      URL.revokeObjectURL(imageUrl);
    }

    setForm(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));
  };

  // Stats
  const stats = {
    total: items.length,
    active: items.filter(i => i.is_active).length,
    inactive: items.filter(i => !i.is_active).length,
    value: items.reduce((s, i) => s + i.base_price * (i.stock_quantity || 0), 0),
  } as const;

  // Filter items based on user role (le reste des filtres est géré côté backend)
  const filtered = isBuyer
    ? items.filter(item => item.is_active) // Buyers see only active products
    : items; // Admins voient tout ce que le backend a déjà filtré

  return (
    <div className="h-full overflow-auto md:overflow-hidden md:flex md:flex-col bg-gradient-to-br from-slate-50 via-blue-50/20 to-slate-50 dark:from-slate-950 dark:via-blue-950/10 dark:to-slate-950">
      <ModuleHeader
        icon={Package}
        title="Produits"
        subtitle={
          <>
            {stats.total} produit{stats.total > 1 ? 's' : ''} • {stats.value.toFixed(2)} {items[0]?.currency || 'EUR'} en stock
          </>
        }
        actions={
          isSuperAdmin && (
            <AddButton
              onClick={() => openModal()}
              className="text-sm"
            />
          )
        }
      />

      {/* Stats Cards */}
      <div className="px-3 py-2 sm:px-6 sm:py-4 grid grid-cols-3 gap-2 sm:gap-4">
        <button
          onClick={() => setStatusFilter('all')}
          className={`group relative overflow-hidden rounded-2xl border ${statusFilter === 'all'
            ? 'border-blue-400 shadow-md bg-white/90 dark:bg-slate-900/90'
            : 'border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 hover:border-blue-200/80'
            } px-2 py-2 sm:px-5 sm:py-4 text-left shadow-sm transition-all duration-200 ease-out`}
        >
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-blue-50/70 to-cyan-50/70 dark:from-blue-950/20 dark:to-cyan-950/20 pointer-events-none transition-opacity duration-200" />
          <div className="relative flex items-center justify-between gap-1 sm:gap-3">
            <div className="flex items-center gap-1.5 sm:gap-3 min-w-0">
              <div className="flex h-7 w-7 sm:h-9 sm:w-9 items-center justify-center rounded-full bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-300 shadow-sm shrink-0">
                <Package className="h-3 w-3 sm:h-4 sm:w-4" />
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Total</span>
                <span className="hidden sm:block text-[11px] text-slate-400 dark:text-slate-500 truncate">Tous les produits</span>
              </div>
            </div>
            <div className="text-lg sm:text-3xl font-bold text-slate-800 dark:text-slate-100 leading-tight">
              {stats.total}
            </div>
          </div>
        </button>
        <button
          onClick={() => setStatusFilter('active')}
          className={`group relative overflow-hidden rounded-2xl border ${statusFilter === 'active'
            ? 'border-green-400 shadow-md bg-white/90 dark:bg-slate-900/90'
            : 'border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 hover:border-green-200/80'
            } px-2 py-2 sm:px-5 sm:py-4 text-left shadow-sm transition-all duration-200 ease-out`}
        >
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-emerald-50/70 to-teal-50/70 dark:from-emerald-950/20 dark:to-teal-950/20 pointer-events-none transition-opacity duration-200" />
          <div className="relative flex items-center justify-between gap-1 sm:gap-3">
            <div className="flex items-center gap-1.5 sm:gap-3 min-w-0">
              <div className="flex h-7 w-7 sm:h-9 sm:w-9 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-300 shadow-sm shrink-0">
                <CheckCircle2 className="h-3 w-3 sm:h-4 sm:w-4" />
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Actifs</span>
                <span className="hidden sm:block text-[11px] text-slate-400 dark:text-slate-500 truncate">Produits actifs</span>
              </div>
            </div>
            <div className="text-lg sm:text-3xl font-bold text-emerald-600 dark:text-emerald-400 leading-tight">
              {stats.active}
            </div>
          </div>
        </button>
        <button
          onClick={() => setStatusFilter('inactive')}
          className={`group relative overflow-hidden rounded-2xl border ${statusFilter === 'inactive'
            ? 'border-slate-400 shadow-md bg-white/90 dark:bg-slate-900/90'
            : 'border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 hover:border-slate-300/80'
            } px-2 py-2 sm:px-5 sm:py-4 text-left shadow-sm transition-all duration-200 ease-out`}
        >
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-slate-50/70 to-slate-100/70 dark:from-slate-900/30 dark:to-slate-800/30 pointer-events-none transition-opacity duration-200" />
          <div className="relative flex items-center justify-between gap-1 sm:gap-3">
            <div className="flex items-center gap-1.5 sm:gap-3 min-w-0">
              <div className="flex h-7 w-7 sm:h-9 sm:w-9 items-center justify-center rounded-full bg-slate-50 text-slate-500 dark:bg-slate-900/60 dark:text-slate-300 shadow-sm shrink-0">
                <XCircle className="h-3 w-3 sm:h-4 sm:w-4" />
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Inactifs</span>
                <span className="hidden sm:block text-[11px] text-slate-400 dark:text-slate-500 truncate">Produits inactifs</span>
              </div>
            </div>
            <div className="text-lg sm:text-3xl font-bold text-slate-600 dark:text-slate-300 leading-tight">
              {stats.inactive}
            </div>
          </div>
        </button>
      </div>

      {/* Filters */}
      {/* Desktop Filters */}
      <div className="hidden md:flex px-6 py-3 flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher par titre, description..." className="w-full h-10 pl-10 pr-4 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            className="h-10 px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-sm"
            value={brandFilter}
            onChange={(e) => setBrandFilter(e.target.value)}
          >
            <option value="all">Toutes les marques</option>
            {sortedBrands.map((brand) => (
              <option key={brand.id} value={brand.name}>{brand.name}</option>
            ))}
          </select>
          <select
            className="h-10 px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-sm"
            value={galenicFilter}
            onChange={(e) => setGalenicFilter(e.target.value)}
          >
            <option value="all">Toutes les formes</option>
            {sortedFormsList.map((formItem) => (
              <option key={formItem.id} value={formItem.name}>{formItem.name}</option>
            ))}
          </select>
          <select
            className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
            value={unitFilter}
            onChange={(e) => setUnitFilter(e.target.value)}
          >
            <option value="all">Toutes les unités</option>
            {sortedUnits.map((unit) => (
              <option key={unit.id} value={unit.name}>{unit.name}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-1 border border-slate-300 dark:border-slate-600 rounded-lg p-1 ml-auto">
          <button onClick={() => setViewMode('grid')} className={`p-2 rounded ${viewMode === 'grid' ? 'bg-blue-100 dark:bg-blue-950/30 text-blue-700' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
            <Grid3x3 className="h-4 w-4" />
          </button>
          <button onClick={() => setViewMode('list')} className={`p-2 rounded ${viewMode === 'list' ? 'bg-blue-100 dark:bg-blue-950/30 text-blue-700' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
            <List className="h-4 w-4" />
          </button>
          <button onClick={() => setViewMode('table')} className={`p-2 rounded ${viewMode === 'table' ? 'bg-blue-100 dark:bg-blue-950/30 text-blue-700' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
            <TableIcon className="h-4 w-4" />
          </button>
          <button onClick={() => setViewMode('category')} className={`p-2 rounded ${viewMode === 'category' ? 'bg-blue-100 dark:bg-blue-950/30 text-blue-700' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`} title="Vue par Catégorie">
            <Layers className="h-4 w-4" />
          </button>
        </div>

        {viewMode === 'table' && (
          <button
            onClick={() => setShowColumnConfigurator(true)}
            className="h-10 px-4 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2"
            title="Gérer les colonnes"
          >
            <Settings className="h-4 w-4" />
            Colonnes
          </button>
        )}

        <button
          onClick={resetAllFilters}
          className="h-10 px-4 rounded-lg border bg-red-50 dark:bg-red-950/20 border-red-300 dark:border-red-800 text-sm text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-950/30 transition-colors flex items-center gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Réinitialiser
        </button>
      </div>

      {/* Mobile Filters */}
      <div className="flex flex-col gap-2 px-3 py-2 md:hidden">
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher..." className="w-full h-9 pl-9 pr-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div className="flex gap-2">
          <select
            className="h-8 flex-1 min-w-0 px-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-xs"
            value={brandFilter}
            onChange={(e) => setBrandFilter(e.target.value)}
          >
            <option value="all">Marques</option>
            {sortedBrands.map((brand) => (
              <option key={brand.id} value={brand.name}>{brand.name}</option>
            ))}
          </select>
          <select
            className="h-8 flex-1 min-w-0 px-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-xs"
            value={galenicFilter}
            onChange={(e) => setGalenicFilter(e.target.value)}
          >
            <option value="all">Formes</option>
            {sortedFormsList.map((formItem) => (
              <option key={formItem.id} value={formItem.name}>{formItem.name}</option>
            ))}
          </select>
          <select
            className="h-8 flex-1 min-w-0 px-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-xs"
            value={unitFilter}
            onChange={(e) => setUnitFilter(e.target.value)}
          >
            <option value="all">Unités</option>
            {sortedUnits.map((unit) => (
              <option key={unit.id} value={unit.name}>{unit.name}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0.5 border border-slate-300 dark:border-slate-600 rounded-lg p-0.5">
            <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-blue-100 dark:bg-blue-950/30 text-blue-700' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
              <Grid3x3 className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => setViewMode('list')} className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-blue-100 dark:bg-blue-950/30 text-blue-700' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
              <List className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => setViewMode('category')} className={`p-1.5 rounded ${viewMode === 'category' ? 'bg-blue-100 dark:bg-blue-950/30 text-blue-700' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`} title="Vue par Catégorie">
              <Layers className="h-3.5 w-3.5" />
            </button>
          </div>
          <button
            onClick={resetAllFilters}
            className="h-8 px-2.5 rounded-lg border bg-red-50 dark:bg-red-950/20 border-red-300 dark:border-red-800 text-xs text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-950/30 transition-colors flex items-center gap-1"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Réinitialiser
          </button>
        </div>
      </div>

      {activeFilters.length > 0 && (
        <div className="px-6 pb-3 flex flex-wrap items-center gap-2 text-sm">
          <span className="text-slate-600 dark:text-slate-300 font-semibold">Filtres actifs:</span>
          {activeFilters.map((tag) => (
            <span key={tag.key} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 text-blue-800 border border-blue-300 shadow-sm dark:bg-blue-950/50 dark:text-blue-200 dark:border-blue-700">
              {tag.label}
              <button
                type="button"
                onClick={tag.onClear}
                className="inline-flex items-center justify-center rounded-full p-0.5 text-blue-700 hover:bg-blue-200 hover:text-blue-900 dark:text-blue-200 dark:hover:bg-blue-900/50"
                aria-label={`Supprimer ${tag.label}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Content */}
      <div className={`md:flex-1 px-3 pb-4 sm:px-6 sm:pb-6 md:min-h-0 ${viewMode === 'table' ? 'md:overflow-hidden' : 'md:overflow-auto'}`}>
        {busy ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-500 dark:text-slate-400">
            <Package className="h-16 w-16 mb-4 opacity-50" />
            <p className="text-lg font-medium">Aucun produit trouvé</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
            {filtered.map((item, idx) => (
              <div
                key={item.id}
                onClick={() => handleCardClick(item)}
                className="group relative p-3 sm:p-5 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:shadow-xl hover:border-blue-300 dark:hover:border-blue-700 hover:scale-[1.02] transition-all duration-300 ease-out animate-in fade-in slide-in-from-bottom-4 cursor-pointer"
                style={{ animationDelay: `${idx * 50}ms`, animationFillMode: 'backwards' }}
              >
                {/* Status Badge and Actions */}
                <div className="flex justify-end items-start mb-2 sm:mb-3">
                  {!isBuyer && (
                    <div className={`flex items-center gap-1 px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium transition-transform group-hover:scale-110 duration-200 ${item.is_active ? 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'}`}>
                      {item.is_active ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                      {item.is_active ? 'Actif' : 'Inactif'}
                    </div>
                  )}
                </div>
                {/* Image Carousel */}
                <div className="w-full aspect-[4/3] sm:aspect-square bg-slate-100 dark:bg-slate-800 rounded-lg mb-2 sm:mb-3 overflow-hidden">
                  <Carousel
                    images={item.images || []}
                    alt={item.title}
                    className="w-full h-full object-cover"
                    showDots={true}
                    showArrows={true}
                    autoPlay={false}
                  />
                </div>
                {/* Info */}
                <h3 className="font-bold text-sm sm:text-base text-slate-900 dark:text-slate-100 mb-0.5 sm:mb-1 truncate">{item.title}</h3>
                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mb-2 sm:mb-3 line-clamp-1 sm:line-clamp-2 sm:min-h-[40px]">{item.description || 'Aucune description'}</p>

                {/* Minimum Order Quantity Badge */}

                <div className="space-y-1 sm:space-y-1.5 mb-2 sm:mb-3">
                  <p className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-400">
                    Prix Catalogue : <span className="font-bold text-cyan-600 text-sm sm:text-lg">{item.base_price.toFixed(2)} € HT</span> <span className="text-slate-500">/ {item.unit}</span>
                  </p>
                  {item.prix_vente_conseille_ttc != null && (
                    <p className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-400">
                      Prix conseillé : <span className="font-bold text-cyan-600">{item.prix_vente_conseille_ttc.toFixed(2)} € TTC</span> <span className="text-slate-500">/ {item.unit}</span>
                    </p>
                  )}
                  <p className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-400">
                    Min. commande : <span className="font-medium">Lot de {item.batch_size || 1} {item.unit}</span>
                  </p>
                </div>
                {/* Actions */}
                {isSuperAdmin && (
                  <div className="flex gap-2 pt-2 sm:pt-3 border-t border-slate-200 dark:border-slate-700 transition-opacity duration-300 justify-end">
                    <button onClick={(e) => { e.stopPropagation(); del(item); }} className="flex items-center justify-center gap-1 p-1.5 sm:px-3 sm:py-2 text-sm bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-100 dark:hover:bg-red-950/50 hover:scale-105 transition-all duration-200">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : viewMode === 'list' ? (
          <div className="space-y-2 sm:space-y-3">
            {filtered.map((item, idx) => (
              <div
                key={item.id}
                onClick={() => handleCardClick(item)}
                className="flex items-center gap-2.5 sm:gap-4 p-2.5 sm:p-4 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:shadow-lg hover:border-blue-300 dark:hover:border-blue-700 transition-all duration-300 ease-out animate-in fade-in slide-in-from-left-4 cursor-pointer"
                style={{ animationDelay: `${idx * 30}ms`, animationFillMode: 'backwards' }}
              >
                <div className="w-14 h-14 sm:w-20 sm:h-20 bg-slate-100 dark:bg-slate-800 rounded-lg overflow-hidden flex-shrink-0">
                  <Carousel
                    images={item.images || []}
                    alt={item.title}
                    className="w-full h-full"
                    showDots={false}
                    showArrows={false}
                    autoPlay={false}
                    compact={true}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
                    <h3 className="font-bold text-sm sm:text-base text-slate-900 dark:text-slate-100 truncate">{item.title}</h3>
                    <div className={`flex items-center gap-1 px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium shrink-0 ${item.is_active ? 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'}`}>
                      {item.is_active ? 'Actif' : 'Inactif'}
                    </div>
                  </div>
                  <p className="text-[11px] sm:text-sm text-slate-500 dark:text-slate-400 line-clamp-1">{item.description || 'Aucune description'}</p>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5 sm:mt-1">
                    <span className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-400"><span className="font-bold text-cyan-600">{item.base_price.toFixed(2)} € HT</span> / {item.unit}</span>
                    {item.prix_vente_conseille_ttc != null && (
                      <span className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-400">· <span className="font-bold text-cyan-600">{item.prix_vente_conseille_ttc.toFixed(2)} € TTC</span></span>
                    )}
                    <span className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-400">· Min: {item.batch_size || 1} {item.unit}</span>
                  </div>
                </div>
                {isSuperAdmin && (
                  <div className="flex gap-2 shrink-0">
                    <button onClick={(e) => { e.stopPropagation(); del(item); }} className="p-1.5 sm:p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : viewMode === 'table' ? (
          <div className="bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 shadow-lg h-full flex flex-col min-h-0">
            <div className="flex-1 overflow-auto min-h-0">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border-b-2 border-blue-200 dark:border-blue-800">
                  <tr>
                    {columns.filter(col => col.visible && col.key !== 'actions').map(col => renderTableHeader(col))}
                    {visibleColumnKeys.includes('actions') && isSuperAdmin && <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {filtered.map((item, idx) => (
                    <tr
                      key={item.id}
                      onClick={() => handleCardClick(item)}
                      className="hover:bg-blue-50/50 dark:hover:bg-blue-950/20 transition-colors duration-200 animate-in fade-in slide-in-from-bottom-2 cursor-pointer"
                      style={{ animationDelay: `${idx * 20}ms`, animationFillMode: 'backwards' }}
                    >
                      {columns.filter(col => col.visible && col.key !== 'actions').map(col => renderTableCell(col, item))}
                      {visibleColumnKeys.includes('actions') && isSuperAdmin && (
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button onClick={(e) => { e.stopPropagation(); del(item); }} className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-950/50 rounded-lg transition-all duration-200 hover:scale-110">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : viewMode === 'category' ? (
          <div className="space-y-3 sm:space-y-4">
            {/* Category count header */}
            <div className="flex justify-end">
              <span className="text-sm text-slate-500 dark:text-slate-400">
                {(() => {
                  const groups: Record<string, Consumable[]> = {};
                  filtered.forEach((item) => {
                    const marque = item.marque || 'Sans marque';
                    const gamme = item.gamme || 'Sans gamme';
                    const unit = item.unit || 'Unité';
                    const key = `${marque}|${gamme}|${unit}`;
                    if (!groups[key]) groups[key] = [];
                    groups[key].push(item);
                  });
                  return Object.keys(groups).length;
                })()} catégorie{(() => {
                  const groups: Record<string, Consumable[]> = {};
                  filtered.forEach((item) => {
                    const marque = item.marque || 'Sans marque';
                    const gamme = item.gamme || 'Sans gamme';
                    const unit = item.unit || 'Unité';
                    const key = `${marque}|${gamme}|${unit}`;
                    if (!groups[key]) groups[key] = [];
                    groups[key].push(item);
                  });
                  return Object.keys(groups).length > 1 ? 's' : '';
                })()}
              </span>
            </div>

            {/* Category groups */}
            {(() => {
              const groups: Record<string, { label: string; products: Consumable[] }> = {};
              filtered.forEach((item) => {
                const marque = item.marque || 'Sans marque';
                const gamme = item.gamme || 'Sans gamme';
                const unit = item.unit || 'Unité';
                const key = `${marque}|${gamme}|${unit}`;
                const label = `${marque} / ${gamme} / ${unit}`;
                if (!groups[key]) groups[key] = { label, products: [] };
                groups[key].products.push(item);
              });
              return Object.entries(groups)
                .sort(([, a], [, b]) => a.label.localeCompare(b.label))
                .map(([key, group]) => {
                  const isExpanded = expandedCategories.has(key);
                  return (
                    <div
                      key={key}
                      className="bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden"
                    >
                      {/* Category Header */}
                      <button
                        onClick={() => {
                          setExpandedCategories((prev) => {
                            const newSet = new Set(prev);
                            if (newSet.has(key)) {
                              newSet.delete(key);
                            } else {
                              newSet.add(key);
                            }
                            return newSet;
                          });
                        }}
                        className="w-full flex items-center justify-between px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-slate-50 to-blue-50/50 dark:from-slate-800 dark:to-blue-950/20 hover:from-slate-100 hover:to-blue-100/50 dark:hover:from-slate-700 dark:hover:to-blue-900/30 transition-colors"
                      >
                        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                          {isExpanded ? (
                            <ChevronUp className="h-4 w-4 sm:h-5 sm:w-5 text-slate-500 shrink-0" />
                          ) : (
                            <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 text-slate-500 shrink-0" />
                          )}
                          <Package className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 dark:text-blue-400 shrink-0" />
                          <span className="font-semibold text-xs sm:text-base text-slate-900 dark:text-slate-100 text-left">
                            {group.label}
                          </span>
                          <span className="text-[11px] sm:text-sm text-slate-500 dark:text-slate-400 shrink-0">
                            ({group.products.length} produit{group.products.length > 1 ? 's' : ''})
                          </span>
                        </div>
                      </button>

                      {/* Expanded content */}
                      {isExpanded && (
                        <div className="p-2 sm:p-3 border-t border-slate-200 dark:border-slate-700">
                          <div className="flex flex-wrap gap-2 sm:gap-3">
                            {group.products.map((item) => (
                              <div
                                key={item.id}
                                onClick={() => handleCardClick(item)}
                                className="group flex w-full sm:w-[360px] items-start gap-2.5 sm:gap-3 p-2.5 sm:p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:shadow-md hover:border-blue-300 dark:hover:border-blue-700 cursor-pointer transition-all duration-200"
                              >
                                {/* Image */}
                                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden flex-shrink-0">
                                  <Carousel
                                    images={item.images || []}
                                    alt={item.title}
                                    className="w-full h-full"
                                    showDots={false}
                                    showArrows={false}
                                    autoPlay={false}
                                    compact={true}
                                  />
                                </div>

                                {/* Product Info */}
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-start justify-between gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
                                    <h3 className="font-semibold text-slate-900 dark:text-slate-100 text-xs sm:text-sm truncate">
                                      {item.title}
                                    </h3>
                                    {!isBuyer && (
                                      <span className={`shrink-0 px-2 py-0.5 rounded-full text-[11px] font-medium ${item.is_active ? 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'}`}>
                                        {item.is_active ? 'Actif' : 'Inactif'}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-400 line-clamp-1">
                                    {item.description || 'Aucune description'}
                                  </p>
                                  <div className="mt-1 sm:mt-2 space-y-0.5 sm:space-y-1">
                                    <div className="flex flex-wrap items-center gap-x-1 text-[11px] sm:text-xs text-slate-600 dark:text-slate-400">
                                      <span>Prix :</span>
                                      <span className="font-bold text-cyan-600 dark:text-cyan-400">
                                        {item.base_price.toFixed(2)} € HT
                                      </span>
                                      <span className="text-slate-500">/ {item.unit}</span>
                                    </div>
                                    {item.prix_vente_conseille_ttc != null && (
                                      <div className="flex flex-wrap items-center gap-x-1 text-[11px] sm:text-xs text-slate-600 dark:text-slate-400">
                                        <span>Prix conseillé :</span>
                                        <span className="font-bold text-cyan-600 dark:text-cyan-400">
                                          {item.prix_vente_conseille_ttc.toFixed(2)} € TTC
                                        </span>
                                        <span className="text-slate-500">/ {item.unit}</span>
                                      </div>
                                    )}
                                    <p className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-400">
                                      Min : <span className="font-medium">Lot de {item.batch_size || 1} {item.unit}</span>
                                    </p>
                                  </div>
                                </div>

                                {/* Actions */}
                                {isSuperAdmin && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); del(item); }}
                                    className="ml-auto p-1.5 sm:p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors shrink-0"
                                  >
                                    <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                });
            })()}
          </div>
        ) : null}
      </div>

      {/* Simple Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-in zoom-in-95 slide-in-from-bottom-4 duration-300">
            <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-900 z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                  <Package className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">{editing ? 'Modifier' : 'Nouveau'} produit</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Renseignez les informations</p>
                </div>
              </div>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Nom du produit *</label>
                  <input
                    className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 ${!form.title.trim()
                      ? 'border-red-500 dark:border-red-400'
                      : 'border-slate-300 dark:border-slate-600'
                      }`}
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="Nom du produit"
                  />
                  {!form.title.trim() && (
                    <p className="text-red-500 text-xs mt-1">Le titre est obligatoire</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Marque</label>
                  <select
                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                    value={form.marque}
                    onChange={(e) => setForm({ ...form, marque: e.target.value })}
                  >
                    <option value="">Sélectionner une marque</option>
                    {sortedBrands.map((brand) => (
                      <option key={brand.id} value={brand.name}>{brand.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Prix catalogue € HT *</label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      className={`w-full pr-10 px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 ${form.base_price <= 0
                        ? 'border-red-500 dark:border-red-400'
                        : 'border-slate-300 dark:border-slate-600'
                        }`}
                      value={form.base_price || ''}
                      onChange={(e) => setForm({ ...form, base_price: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400">€</span>
                  </div>
                  {form.base_price <= 0 && (
                    <p className="text-red-500 text-xs mt-1">Le prix doit être supérieur à 0</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Prix de vente conseillé € TTC</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                    value={form.prix_vente_conseille_ttc ?? ''}
                    onChange={(e) => setForm({ ...form, prix_vente_conseille_ttc: e.target.value === '' ? null : parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Gamme</label>
                  <select
                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                    value={form.gamme}
                    onChange={(e) => setForm({ ...form, gamme: e.target.value })}
                  >
                    <option value="">Sélectionner une gamme</option>
                    {sortedRanges.map((range) => (
                      <option key={range.id} value={range.name}>{range.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Unité</label>
                  <select className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}>
                    <option value="">Sélectionner une unité</option>
                    {sortedUnits.map((unit) => (
                      <option key={unit.id} value={unit.name}>{unit.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Statut</label>
                  <select className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={form.is_active ? 'active' : 'inactive'} onChange={(e) => setForm({ ...form, is_active: e.target.value === 'active' })}>
                    <option value="active">Actif</option>
                    <option value="inactive">Inactif</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Forme galénique</label>
                  <select
                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                    value={form.forme_galenique}
                    onChange={(e) => setForm({ ...form, forme_galenique: e.target.value })}
                  >
                    <option value="">Sélectionner une forme</option>
                    {sortedFormsList.map((formItem) => (
                      <option key={formItem.id} value={formItem.name}>{formItem.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Conditionnement</label>
                  <select
                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                    value={form.conditionnement}
                    onChange={(e) => setForm({ ...form, conditionnement: e.target.value })}
                  >
                    {packagings.length === 0 && <option value="">Sélectionner un conditionnement</option>}
                    {sortedPackagings.map((packaging) => (
                      <option key={packaging.id} value={packaging.name}>{packaging.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Qté par Lot ({form.unit})</label>
                  <input
                    type="number"
                    min="1"
                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                    value={form.batch_size ?? ''}
                    onChange={(e) => setForm({ ...form, batch_size: e.target.value ? parseInt(e.target.value, 10) : null })}
                    placeholder={`1 ${form.unit}`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Minimum de commande (Lot)</label>
                  <input
                    type="number"
                    min="1"
                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                    value={form.min_order_quantity ?? ''}
                    onChange={(e) => setForm({ ...form, min_order_quantity: e.target.value ? parseInt(e.target.value, 10) : null })}
                    placeholder={`Ex: 12 ${form.unit}`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Code barre GTIN</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                    value={form.code_barre}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/\D/g, '');
                      const trimmed = raw.slice(0, 14);
                      setForm({ ...form, code_barre: trimmed });
                    }}
                    placeholder="Ex: 3760428070029"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">QR code</label>
                  {form.qr_code && (form.qr_code.startsWith('http') || form.qr_code.includes('/uploads/')) ? (
                    <div className="flex items-center gap-3 p-3 border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600">
                      <div className="flex items-center justify-center w-24 h-24 border rounded-lg bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 p-2">
                        <img
                          src={form.qr_code}
                          alt="QR Code"
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-slate-600 dark:text-slate-400">QR code uploadé</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, qr_code: '' })}
                        className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-950/50 rounded-lg transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          className="flex-1 px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                          value={form.qr_code}
                          onChange={(e) => setForm({ ...form, qr_code: e.target.value })}
                          placeholder="URL ou texte pour générer QR code"
                        />
                        {form.qr_code && (
                          <div className="flex items-center justify-center w-20 h-20 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 p-1">
                            <img
                              src={`https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=${encodeURIComponent(form.qr_code)}`}
                              alt="QR Code"
                              className="w-full h-full"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {!(form.qr_code && (form.qr_code.startsWith('http') || form.qr_code.includes('/uploads/'))) && (
                  <div className="pt-6">
                    <div className="relative">
                      <div className="absolute -top-5 left-0 right-0 flex items-center gap-2">
                        <div className="h-px flex-1 bg-slate-300 dark:bg-slate-600"></div>
                        <span className="text-xs text-slate-500">ou</span>
                        <div className="h-px flex-1 bg-slate-300 dark:bg-slate-600"></div>
                      </div>
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const formData = new FormData();
                              formData.append('file', file);
                              try {
                                const res = await api.post('/upload-image', formData, {
                                  headers: { 'Content-Type': 'multipart/form-data' }
                                });
                                setForm({ ...form, qr_code: res.data.url });
                              } catch (err) {
                                console.error('Upload error:', err);
                                showError('Erreur lors de l\'upload de l\'image QR code');
                              }
                            }
                          }}
                          className="hidden"
                          id="qr-upload"
                        />
                        <label
                          htmlFor="qr-upload"
                          className="flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-blue-300 dark:border-blue-700 rounded-lg cursor-pointer hover:border-blue-400 transition-colors text-sm text-slate-600 dark:text-slate-400"
                        >
                          <Upload className="h-4 w-4" />
                          Uploader une image QR code
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea rows={3} className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>

              {/* Section Images */}
              <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                <label className="block text-sm font-medium mb-3 flex items-center gap-2">
                  <ImageIcon className="h-4 w-4 text-blue-600" />
                  Images ({form.images.length})
                </label>

                {/* Preview des images */}
                {form.images.length > 0 && (
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    {form.images.map((img, i) => (
                      <div key={i} className="relative group aspect-square rounded-lg overflow-hidden border-2 border-slate-200 dark:border-slate-700">
                        <img src={img} alt="" className="w-full h-full object-cover" />
                        <button
                          onClick={() => removeImage(i)}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          type="button"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload zone */}
                <div className="space-y-3">
                  <div className="border-2 border-dashed border-blue-300 dark:border-blue-700 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="file-upload"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer block">
                      <Upload className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        Cliquez pour uploader des images
                      </p>
                      <p className="text-xs text-slate-500 mt-1">PNG, JPG, GIF jusqu'à 10MB</p>
                    </label>
                  </div>

                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-slate-200 dark:border-slate-700">
                <button onClick={() => setShowModal(false)} className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">Annuler</button>
                <button
                  onClick={save}
                  disabled={!form.title.trim() || form.base_price <= 0}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${!form.title.trim() || form.base_price <= 0
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                >
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Viewer Modal */}
      {imageViewer.open && form.images.length > 0 && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-[60] p-4 animate-in fade-in duration-200" onClick={() => setImageViewer({ open: false, index: 0 })}>
          <div className="relative max-w-4xl w-full animate-in zoom-in-90 duration-300" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setImageViewer({ open: false, index: 0 })}
              className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
            <img
              src={form.images[imageViewer.index]}
              alt=""
              className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
            />
            {form.images.length > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                {form.images.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setImageViewer({ open: true, index: i })}
                    className={`w-2 h-2 rounded-full transition-colors ${i === imageViewer.index ? 'bg-white' : 'bg-white/30'}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Product Details Modal */}
      {showDetailsModal && selectedProduct && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto animate-in zoom-in-95 slide-in-from-bottom-4 duration-300">
            <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-900 z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                  <Package className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Détails du produit</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{selectedProduct.title}</p>
                </div>
              </div>
              <button
                onClick={closeDetailsModal}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Product Images */}
              {selectedProduct.images && selectedProduct.images.length > 0 && (
                <div className="w-full aspect-video bg-slate-100 dark:bg-slate-800 rounded-lg overflow-hidden">
                  <Carousel
                    images={selectedProduct.images}
                    alt={selectedProduct.title}
                    className="w-full h-full"
                    showDots={true}
                    showArrows={true}
                    autoPlay={false}
                  />
                </div>
              )}

              {/* Product Identity Card - Carte d'identité du produit */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 rounded-xl p-6 border-2 border-blue-200 dark:border-blue-800">
                <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-6 flex items-center gap-2">
                  <Package className="h-6 w-6 text-blue-600" />
                  Carte d'identité du produit
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                  {/* Nom du produit */}
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">Nom du produit</label>
                    <p className="text-lg font-bold text-slate-900 dark:text-slate-100">{selectedProduct.title}</p>
                  </div>

                  {/* Prix catalogue HT */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">Prix catalogue HT</label>
                    <p className="text-2xl font-bold text-blue-600">
                      {selectedProduct.base_price.toFixed(2)} € HT
                    </p>
                    <p className="text-sm text-slate-500">/ {selectedProduct.unit}</p>
                  </div>

                  {/* Prix de vente conseillé TTC */}
                  {selectedProduct.prix_vente_conseille_ttc != null && (
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">Prix de vente conseillé TTC</label>
                      <p className="text-2xl font-bold text-blue-600">
                        {selectedProduct.prix_vente_conseille_ttc.toFixed(2)} € TTC
                      </p>
                      <p className="text-sm text-slate-500">/ {selectedProduct.unit}</p>
                    </div>
                  )}

                  {/* Référence */}
                  {selectedProduct.reference && (
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">Référence</label>
                      <p className="text-lg font-semibold text-slate-700 dark:text-slate-300">{selectedProduct.reference}</p>
                    </div>
                  )}

                  {/* Gamme */}
                  {selectedProduct.gamme && (
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">Gamme</label>
                      <p className="text-slate-700 dark:text-slate-300">{selectedProduct.gamme}</p>
                    </div>
                  )}

                  {/* Marque */}
                  {selectedProduct.marque && (
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">Marque</label>
                      <p className="text-slate-700 dark:text-slate-300">{selectedProduct.marque}</p>
                    </div>
                  )}

                  {/* Unité */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">Unité</label>
                    <p className="text-slate-700 dark:text-slate-300">{selectedProduct.unit}</p>
                  </div>

                  {/* Lot de commande minimum */}
                  {selectedProduct.min_order_quantity && selectedProduct.min_order_quantity > 1 && (
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">Commande minimum</label>
                      <p className="text-lg font-bold text-orange-600 dark:text-orange-400">
                        📦 {selectedProduct.min_order_quantity} {selectedProduct.min_order_quantity === 1 ? 'Lot' : 'Lots'} de {selectedProduct.batch_size} {selectedProduct.unit}
                      </p>
                    </div>
                  )}

                  {/* Statut */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">Statut</label>
                    <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${selectedProduct.is_active ? 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'}`}>
                      {selectedProduct.is_active ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                      {selectedProduct.is_active ? 'Actif' : 'Inactif'}
                    </div>
                  </div>

                  {/* Forme galénique */}
                  {selectedProduct.forme_galenique && (
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">Forme galénique</label>
                      <p className="text-slate-700 dark:text-slate-300">{selectedProduct.forme_galenique}</p>
                    </div>
                  )}

                  {/* Conditionnement */}
                  {selectedProduct.conditionnement && (
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">Conditionnement</label>
                      <p className="text-slate-700 dark:text-slate-300">{selectedProduct.conditionnement}</p>
                    </div>
                  )}

                  {/* Code barre GTIN */}
                  {selectedProduct.code_barre && (
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">Code barre GTIN</label>
                      <p className="text-slate-700 dark:text-slate-300 font-mono">{selectedProduct.code_barre}</p>
                    </div>
                  )}

                  {/* QR code */}
                  {selectedProduct.qr_code && (
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">QR code</label>
                      <div className="flex items-center gap-3">
                        <img
                          src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(selectedProduct.qr_code)}`}
                          alt="QR Code"
                          className="w-24 h-24 border border-slate-300 dark:border-slate-600 rounded p-1 bg-white"
                        />
                        <p className="text-xs text-slate-500 dark:text-slate-400 break-all">{selectedProduct.qr_code}</p>
                      </div>
                    </div>
                  )}

                  {/* Description */}
                  {selectedProduct.description && (
                    <div className="md:col-span-2">
                      <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">Description</label>
                      <p className="text-slate-700 dark:text-slate-300">{selectedProduct.description}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                <button
                  onClick={closeDetailsModal}
                  className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                >
                  Fermer
                </button>
                {isSuperAdmin && (
                  <button
                    onClick={() => {
                      openModal(selectedProduct);
                      closeDetailsModal();
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
                  >
                    <Edit className="h-4 w-4" />
                    Modifier ce produit
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open, item: open ? deleteDialog.item : null })}
        title="Confirmer la suppression"
        description={`Êtes-vous sûr de vouloir supprimer le produit "${deleteDialog.item?.title}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        isLoading={isDeleting}
        onConfirm={handleConfirmDelete}
      />

      {/* Column Configurator Modal */}
      <ColumnConfigurator
        isOpen={showColumnConfigurator}
        onClose={() => setShowColumnConfigurator(false)}
        columns={columns}
        onChange={setColumns}
        storageKey="consumables_table_columns"
      />
    </div>
  );
};

export default ConsumablesTailwind;
